<template>
  <!-- Bootstrap modal overlay -->
  <div v-if="visible" class="modal d-block" tabindex="-1" @click.self="cancel">
    <div class="modal-dialog modal-dialog-centered">
      <div
        :class="[
          'modal-content',
          'shadow',
          'border',
          'border-2',
          isCover ? 'border-primary' : 'border-danger',
        ]"
      >
        <!-- Header -->
        <div
          :class="[
            'modal-header',
            'border-bottom',
            isCover
              ? 'bg-primary bg-opacity-10 border-primary'
              : 'bg-danger bg-opacity-10 border-danger',
          ]"
        >
          <h5 :class="['modal-title', 'fw-bold', isCover ? 'text-primary' : 'text-danger']">
            <i class="fas fa-exclamation-triangle me-2"></i>{{ headerTitle }}
          </h5>
          <button type="button" class="btn-close" :disabled="submitting" @click="cancel" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- Warning banner -->
          <div
            :class="[
              'alert',
              'py-2',
              'mb-3',
              isCover ? 'alert-primary' : 'alert-danger',
            ]"
          >
            <i class="fas fa-radiation-alt me-2"></i>
            <strong>Irreversible action.</strong>
            {{ warningBody }}
          </div>

          <!-- API error -->
          <div v-if="errorMessage" class="alert alert-warning py-2 mb-3">
            <i class="fas fa-exclamation-circle me-2"></i>{{ errorMessage }}
          </div>

          <!-- Order summary -->
          <table class="table table-sm table-borderless mb-3">
            <tbody>
              <tr>
                <td class="text-muted fw-semibold">Ticker</td>
                <td class="fw-bold fs-5">{{ ticker }}</td>
              </tr>
              <tr>
                <td class="text-muted fw-semibold">Quantity</td>
                <td>{{ quantity.toLocaleString() }} shares</td>
              </tr>
              <tr>
                <td class="text-muted fw-semibold">Current Price</td>
                <td>{{ formatCurrency(currentPrice) }}</td>
              </tr>
              <tr class="border-top">
                <td class="text-muted fw-semibold">{{ estimateLabel }}</td>
                <td :class="['fw-bold', isCover ? 'text-primary' : 'text-success']">
                  {{ formatCurrency(estimatedAmount) }}
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Confirmation input -->
          <div class="mb-1">
            <label :class="['form-label', 'fw-semibold', isCover ? 'text-primary' : 'text-danger']">
              Type <strong>{{ ticker }}</strong> to enable the confirm button
            </label>
            <input
              v-model="confirmationInput"
              type="text"
              :class="['form-control', isCover ? 'border-primary' : 'border-danger']"
              :placeholder="ticker"
              :disabled="submitting"
              autocomplete="off"
              spellcheck="false"
            />
          </div>
        </div>

        <div
          :class="[
            'modal-footer',
            'border-top',
            'border-opacity-25',
            isCover ? 'border-primary' : 'border-danger',
          ]"
        >
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="submitting"
            @click="cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            :class="['btn', isCover ? 'btn-primary' : 'btn-danger']"
            :disabled="!confirmationMatches || submitting"
            @click="confirmSell"
          >
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status">
              <span class="visually-hidden">Submitting…</span>
            </span>
            <i
              v-else
              :class="['fas', isCover ? 'fa-arrow-up' : 'fa-arrow-down', 'me-2']"
            ></i>
            {{ buttonLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- Backdrop -->
  <div v-if="visible" class="modal-backdrop fade show"></div>
</template>

<script>
export default {
  name: 'SellConfirmationModal',

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    ticker: {
      type: String,
      required: true,
    },
    quantity: {
      type: Number,
      required: true,
    },
    currentPrice: {
      type: Number,
      required: true,
    },
    side: {
      type: String,
      default: 'LONG',
      validator: (v) => ['LONG', 'SHORT'].includes((v || '').toUpperCase()),
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },

  emits: ['confirm', 'cancel'],

  data() {
    return {
      confirmationInput: '',
      submitting: false,
    };
  },

  watch: {
    errorMessage(val) {
      if (val) this.submitting = false;
    },
  },

  computed: {
    isCover() {
      return (this.side || '').toUpperCase() === 'SHORT';
    },
    estimatedAmount() {
      return this.quantity * this.currentPrice;
    },
    headerTitle() {
      return this.isCover
        ? 'Confirm Market Buy-to-Cover Order'
        : 'Confirm Market Sell Order';
    },
    warningBody() {
      return this.isCover
        ? 'This will immediately place a market BUY order to close the short position. It cannot be undone.'
        : 'This will immediately place a market SELL order. It cannot be undone.';
    },
    estimateLabel() {
      return this.isCover ? 'Est. Cost' : 'Est. Proceeds';
    },
    buttonLabel() {
      if (this.submitting) return 'Submitting…';
      return this.isCover ? 'Confirm Buy' : 'Confirm Sell';
    },
    confirmationMatches() {
      return this.confirmationInput.trim().toLowerCase() === this.ticker.toLowerCase();
    },
  },

  mounted() {
    console.debug(`[SellConfirmationModal] mounted for ticker: ${this.ticker}`);
  },

  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    },

    resetState() {
      this.confirmationInput = '';
      this.submitting = false;
    },

    cancel() {
      console.debug(`[SellConfirmationModal] cancel for ticker: ${this.ticker}`);
      this.resetState();
      this.$emit('cancel');
    },

    confirmSell() {
      if (!this.confirmationMatches || this.submitting) return;
      this.submitting = true;
      console.debug(`[SellConfirmationModal] confirm for ticker: ${this.ticker}, side: ${this.side}`);
      this.$emit('confirm', { ticker: this.ticker, qty: this.quantity });
    },
  },
};
</script>

<style scoped>
.modal {
  background: transparent;
}
</style>
